import { MEASURE_CONFIG, MEASURE_TYPES, getMeasureLabel, getMeasureUnit } from './measurementConfig';

export const MONTH_NAMES_EN = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export const MONTH_SHORT_EN = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const MONTH_KEYS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
];

export const MONTH_SHORT_KEYS = [
    'jan', 'feb', 'mar', 'apr', 'mayShort', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
];

/**
 * Get localized month name (full)
 */
export const getMonthName = (monthNumber, t) => {
    if (monthNumber < 1 || monthNumber > 12) return `Month ${monthNumber}`;
    const key = `common.${MONTH_KEYS[monthNumber - 1]}`;
    return t ? t(key, MONTH_NAMES_EN[monthNumber - 1]) : MONTH_NAMES_EN[monthNumber - 1];
};

/**
 * Get localized short month name
 */
export const getMonthShortName = (monthNumber, t) => {
    if (monthNumber < 1 || monthNumber > 12) return `M${monthNumber}`;
    const key = `common.${MONTH_SHORT_KEYS[monthNumber - 1]}`;
    return t ? t(key, MONTH_SHORT_EN[monthNumber - 1]) : MONTH_SHORT_EN[monthNumber - 1];
};

/**
 * Flatten and normalize raw monthly averages response
 * Each item in response has: { year, month, zone, measures: [{ type, value }] }
 */
export const normalizeMonthlyData = (rawList = []) => {
    if (!Array.isArray(rawList)) return [];

    return rawList.map((item) => {
        const measureMap = {};
        if (Array.isArray(item.measures)) {
            item.measures.forEach((m) => {
                if (m && m.type) {
                    const parsed = parseFloat(m.value);
                    measureMap[m.type] = isNaN(parsed) ? null : parsed;
                }
            });
        }

        return {
            year: item.year,
            month: item.month,
            zone: item.zone || 'Unassigned',
            measures: measureMap,
            rawMeasures: item.measures || [],
        };
    });
};

/**
 * Calculate KPI statistics for a given measurement type or overall
 */
export const calculateKpiStats = (normalizedData = [], activeMeasure = 'ALL', t) => {
    if (!normalizedData || normalizedData.length === 0) {
        return null;
    }

    const measuresToAnalyze = activeMeasure === 'ALL' ? MEASURE_TYPES : [activeMeasure];
    const statsByMeasure = {};

    measuresToAnalyze.forEach((type) => {
        const recordsWithValue = normalizedData
            .filter((d) => d.measures[type] !== undefined && d.measures[type] !== null)
            .map((d) => ({
                value: d.measures[type],
                month: d.month,
                zone: d.zone,
                year: d.year,
            }));

        if (recordsWithValue.length === 0) {
            statsByMeasure[type] = null;
            return;
        }

        let minRecord = recordsWithValue[0];
        let maxRecord = recordsWithValue[0];
        let sum = 0;

        recordsWithValue.forEach((r) => {
            if (r.value < minRecord.value) minRecord = r;
            if (r.value > maxRecord.value) maxRecord = r;
            sum += r.value;
        });

        const avg = sum / recordsWithValue.length;

        statsByMeasure[type] = {
            type,
            count: recordsWithValue.length,
            avg: avg,
            min: minRecord.value,
            minDetails: {
                month: minRecord.month,
                monthName: getMonthName(minRecord.month, t),
                zone: minRecord.zone,
            },
            max: maxRecord.value,
            maxDetails: {
                month: maxRecord.month,
                monthName: getMonthName(maxRecord.month, t),
                zone: maxRecord.zone,
            },
            unit: getMeasureUnit(type),
            label: getMeasureLabel(type, t),
            color: MEASURE_CONFIG[type]?.color,
        };
    });

    const uniqueZones = new Set(normalizedData.map((d) => d.zone)).size;
    const totalRecords = normalizedData.length;

    return {
        statsByMeasure,
        uniqueZones,
        totalRecords,
        primaryStat: activeMeasure !== 'ALL' ? statsByMeasure[activeMeasure] : statsByMeasure['TEMP'] || Object.values(statsByMeasure).find(Boolean) || null,
    };
};

/**
 * Transform data for 12-Month Trend Chart
 * When activeMeasure is a specific metric (e.g. TEMP), output has month keys and zone values
 * When activeMeasure is 'ALL' or comparing metrics, output has month keys and metric values (averaged across zones)
 */
export const build12MonthTrendData = (normalizedData = [], { activeMeasure = 'ALL', selectedZone = 'all', t } = {}) => {
    // 1 to 12 months array template
    const monthsData = Array.from({ length: 12 }, (_, i) => {
        const monthNum = i + 1;
        return {
            month: monthNum,
            monthName: getMonthName(monthNum, t),
            monthShort: getMonthShortName(monthNum, t),
        };
    });

    const zones = [...new Set(normalizedData.map((d) => d.zone))].filter(Boolean).sort();

    if (activeMeasure !== 'ALL' && selectedZone === 'all') {
        // Multi-zone comparison for a single metric
        normalizedData.forEach((item) => {
            const mIndex = item.month - 1;
            if (mIndex >= 0 && mIndex < 12) {
                const val = item.measures[activeMeasure];
                if (val !== undefined && val !== null) {
                    monthsData[mIndex][item.zone] = val;
                }
            }
        });

        return {
            chartData: monthsData,
            seriesKeys: zones,
            seriesType: 'zone',
            measureType: activeMeasure,
        };
    } else if (selectedZone !== 'all' || activeMeasure === 'ALL') {
        // Single zone or Multi-metric comparison
        // Group by month
        const monthGroups = {};
        for (let m = 1; m <= 12; m++) {
            monthGroups[m] = {};
            MEASURE_TYPES.forEach((type) => {
                monthGroups[m][type] = [];
            });
        }

        normalizedData.forEach((item) => {
            if (selectedZone !== 'all' && item.zone !== selectedZone) {
                return;
            }
            const m = item.month;
            if (m >= 1 && m <= 12) {
                MEASURE_TYPES.forEach((type) => {
                    const val = item.measures[type];
                    if (val !== undefined && val !== null) {
                        monthGroups[m][type].push(val);
                    }
                });
            }
        });

        // Compute averages for each metric in each month
        for (let m = 1; m <= 12; m++) {
            const mIndex = m - 1;
            MEASURE_TYPES.forEach((type) => {
                const arr = monthGroups[m][type];
                if (arr.length > 0) {
                    const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
                    monthsData[mIndex][type] = parseFloat(avg.toFixed(2));
                }
            });
        }

        const seriesKeys = activeMeasure === 'ALL' ? MEASURE_TYPES : [activeMeasure];

        return {
            chartData: monthsData,
            seriesKeys,
            seriesType: 'metric',
            measureType: activeMeasure,
        };
    }

    return {
        chartData: monthsData,
        seriesKeys: zones,
        seriesType: 'zone',
        measureType: activeMeasure,
    };
};

/**
 * Transform data for Single Month Zone Comparison (Bar Chart)
 */
export const buildMonthZoneComparisonData = (normalizedData = [], { activeMeasure = 'ALL', t } = {}) => {
    // Unique zones
    const zoneMap = {};

    normalizedData.forEach((item) => {
        const zone = item.zone;
        if (!zoneMap[zone]) {
            zoneMap[zone] = {
                zone,
            };
        }

        MEASURE_TYPES.forEach((type) => {
            const val = item.measures[type];
            if (val !== undefined && val !== null) {
                zoneMap[zone][type] = val;
            }
        });
    });

    const chartData = Object.values(zoneMap).sort((a, b) => a.zone.localeCompare(b.zone));
    const seriesKeys = activeMeasure === 'ALL' ? MEASURE_TYPES : [activeMeasure];

    return {
        chartData,
        seriesKeys,
    };
};

/**
 * Generate and trigger CSV download of monthly sensor averages
 */
export const exportMonthlyDataToCsv = (normalizedData = [], year, t) => {
    if (!normalizedData || normalizedData.length === 0) return;

    const headers = [
        'Year',
        'Month',
        'Month Name',
        'Zone',
        'Temperature (°C)',
        'Humidity (%)',
        'Power (kW)',
        'Pressure (hPa)',
    ];

    const rows = normalizedData.map((item) => {
        return [
            item.year,
            item.month,
            getMonthName(item.month, t),
            `"${(item.zone || '').replace(/"/g, '""')}"`,
            item.measures.TEMP !== undefined && item.measures.TEMP !== null ? item.measures.TEMP : '',
            item.measures.HUMI !== undefined && item.measures.HUMI !== null ? item.measures.HUMI : '',
            item.measures.POWE !== undefined && item.measures.POWE !== null ? item.measures.POWE : '',
            item.measures.PRES !== undefined && item.measures.PRES !== null ? item.measures.PRES : '',
        ];
    });

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sensor_monthly_averages_${year || 'all'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Distinct vibrant color palette for zone series in Recharts
 */
export const ZONE_COLORS = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#14b8a6', // teal
    '#84cc16', // lime
    '#f97316', // orange
    '#6366f1', // indigo
    '#d946ef', // fuchsia
];

export const getZoneColor = (index) => {
    return ZONE_COLORS[index % ZONE_COLORS.length];
};
